import { useEffect, useMemo, useRef, useState } from "react";
import YhButton from "../../../../ui/YhButton";
import { getAllUsers } from "../../../Team/components/ShowAll/ShowAllService";
import { openErrorNotification } from "../../../../helpers/methods";
import {
  CreateBasic,
  getProjectDetails,
  googleAccount,
  googleAuth,
  UpdateBasic,
} from "../../Service/Service";
import profileImg from "../../../../assets/images/user-default.svg";
import history from "../../../../utils/history";
import urls from "../../../../global/constants/UrlConstants";
import OrganizationSelect from "../../../../Shared/Organization/OrganizationSelect";
import { store } from "../../../../utils/store";

interface CustomProps {
  nextStep?: () => void | undefined;
  userId: String;
  setUserId?: Function;
  fromSettingOrLogout?: any;
  state?: any;
  setStateData?: any;
}

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    className={`transition-transform ${open ? "rotate-180" : ""}`}
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const HostRow = ({
  user,
  selected,
}: {
  user: any;
  selected?: boolean;
}) => (
  <div className="flex min-w-0 items-center gap-3">
    <img
      src={user.image || profileImg}
      alt=""
      className="h-10 w-10 shrink-0 rounded-full object-cover bg-[#F3F4F6]"
    />
    <div className="min-w-0 flex-1">
      <p
        className={`truncate text-sm ${
          selected ? "font-semibold text-[#111827]" : "font-medium text-[#111827]"
        }`}
      >
        {user.label}
      </p>
      <p className="truncate text-xs text-[#6B7280]">
        {[user.department, user.role].filter(Boolean).join(", ") || "—"}
      </p>
    </div>
  </div>
);

const GoogleSvg = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const BasicSetup = (props: CustomProps) => {
  const [userData, setUserData] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [hostOpen, setHostOpen] = useState(false);
  const [hostQuery, setHostQuery] = useState("");
  const hostRef = useRef<HTMLDivElement | null>(null);
  const hostSearchRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    tableDataShowHandler();
    getEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hostOpen) {
      setHostQuery("");
      return;
    }
    const onDoc = (e: MouseEvent) => {
      if (hostRef.current && !hostRef.current.contains(e.target as Node)) {
        setHostOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setHostOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    requestAnimationFrame(() => hostSearchRef.current?.focus());
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [hostOpen]);

  const tableDataShowHandler = async () => {
    try {
      const auth = store.getState().auth || {};
      const loginUserId = auth.userId ? String(auth.userId) : "";
      const teamUser = await getAllUsers({ page: 1, limit: 1000 });
      let Userresponse = (teamUser.data || [])?.map((item: any) => ({
        id: String(item._id),
        label: item.name,
        image: item.profile_image,
        role: item.userOffices?.[0]?.teamRoleName,
        department: item.userOffices?.[0]?.department,
        value: String(item._id),
      }));

      // Ensure the logged-in user is always available as host
      if (
        loginUserId &&
        !Userresponse.some((u: any) => String(u.id) === loginUserId)
      ) {
        Userresponse = [
          {
            id: loginUserId,
            label: auth.userName || "You",
            image: undefined,
            role: auth.role,
            department: undefined,
            value: loginUserId,
          },
          ...Userresponse,
        ];
      }

      setUserData(Userresponse);

      // Default / lock host to the logged-in person
      if (loginUserId && !props.state?.meetingId) {
        props.setStateData?.({ host: loginUserId });
      } else if (loginUserId && !props.state?.host) {
        props.setStateData?.({ host: loginUserId });
      }
    } catch (error: any) {
      openErrorNotification(error.message);
    }
  };

  useEffect(() => {
    if (props.state?.host) {
      getProjects();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.state?.host]);

  const getProjects = async () => {
    const response = await getProjectDetails({
      status: "1",
      userId: props.state?.host,
    });
    setProjects(response.data || []);
  };

  const goBack = () => {
    history.push(urls.meetingListViewPath);
  };

  const submitForm = async () => {
    try {
      const loginUserId = store.getState().auth?.userId
        ? String(store.getState().auth.userId)
        : "";
      // Host must be the logged-in user
      const hostId = loginUserId || String(props.state?.host || "");

      if (!hostId) {
        openErrorNotification("Please select a host");
        return;
      }

      if (!props.state?.calender?.[0]) {
        openErrorNotification("Please connect Google Calendar first");
        return;
      }

      const meetingType = props.state?.meetingType || "both";

      if (props.state?.meetingId) {
        await UpdateBasic({
          meetingId: props.state.meetingId,
          host: hostId,
          calendar: props.state.calender?.[0],
          organizationId: props.state.organizationId,
          meetingType,
        });
        props.nextStep?.();
        return;
      }

      const response = await CreateBasic({
        host: hostId,
        calendar: props.state.calender?.[0],
        organizationId: props.state.organizationId,
        meetingType,
      });

      if (
        response?.code === "HOST_MEETING_EXISTS" ||
        response?.existingMeetingId
      ) {
        const existingId = String(response.existingMeetingId);
        openErrorNotification(
          "You already have a meeting. Opening it for edit…"
        );
        history.replace({
          pathname: urls.createMeetingViewPath,
          state: { meetingId: existingId, mode: "edit" },
        });
        return;
      }

      const meetingId =
        response?.meetingId ||
        response?.data?.meetingId ||
        response?.data?._id ||
        response?._id;

      if (!meetingId) {
        openErrorNotification(
          response?.message || "Failed to save basic setup. Please try again."
        );
        return;
      }

      props.setStateData?.({ meetingId, host: hostId });
      props.nextStep?.();
    } catch (error: any) {
      openErrorNotification(
        error?.message || "Failed to save basic setup. Please try again."
      );
    }
  };

  const getEmail = async () => {
    const response = await googleAccount();
    const emails = response.data?.map((acc: any) => acc.email);
    props.setStateData({ calender: emails });
  };

  async function initiateGoogleAuth() {
    try {
      const data = await googleAuth();
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (err: any) {
      console.error("Google Auth Error:", err);
    }
  }

  const loginUserId = store.getState().auth?.userId
    ? String(store.getState().auth.userId)
    : "";
  const hostValue = String(props.state?.host || loginUserId || "");
  const selectedHost =
    userData.find((u) => String(u.id) === hostValue) ||
    (loginUserId
      ? {
          id: loginUserId,
          label: store.getState().auth?.userName || "You",
          image: undefined,
          role: store.getState().auth?.role,
          department: undefined,
        }
      : null);
  // Host is the logged-in user — no picking another person
  const hostLocked = true;
  const filteredHosts = useMemo(() => {
    const q = hostQuery.trim().toLowerCase();
    if (!q) return userData;
    return userData.filter((u) => {
      const hay = `${u.label} ${u.department || ""} ${u.role || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [userData, hostQuery]);

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden rounded-2xl border border-[#EEF0F5] bg-white shadow-[0_4px_20px_rgba(15,23,42,0.04)]">
      <div className="min-h-0 flex-1 overflow-auto p-5 sm:p-6">
        <div className="mb-5 border-b border-[#F3F4F6] pb-4">
          <h1 className="text-xl font-bold tracking-tight text-[#111827]">
            Basic Setup
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            Provide essential information
          </p>
        </div>

        <div className="flex max-w-3xl flex-col gap-5">
          <OrganizationSelect
            label="Organization"
            required
            value={props.state?.organizationId || ""}
            onChange={(organizationId) => props.setStateData?.({ organizationId })}
          />

          <div>
            <label className="yh-label">
              Meeting Type <span className="text-red-500">*</span>
            </label>
            <p className="mb-2 text-xs text-[#6B7280]">
              Choose which appointment types customers can book
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {[
                {
                  value: "telephonic",
                  label: "Telephonic",
                  desc: "Phone call only",
                },
                {
                  value: "virtual",
                  label: "Virtual",
                  desc: "Google Meet link",
                },
                {
                  value: "both",
                  label: "Both",
                  desc: "Customer chooses",
                },
              ].map((opt) => {
                const selected = (props.state?.meetingType || "both") === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => props.setStateData?.({ meetingType: opt.value })}
                    className={`rounded-xl border px-3 py-3 text-left transition ${
                      selected
                        ? "border-[#A02448] bg-[#F9F0F4] shadow-[0_0_0_2px_rgba(91,95,239,0.15)]"
                        : "border-[#E5E7EB] bg-white hover:border-[#D4B8C0] hover:bg-[#F8FAFC]"
                    }`}
                  >
                    <p
                      className={`text-sm font-semibold ${
                        selected ? "text-[#A02448]" : "text-[#111827]"
                      }`}
                    >
                      {opt.label}
                    </p>
                    <p className="mt-0.5 text-xs text-[#6B7280]">{opt.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="relative w-full" ref={hostRef}>
            <label className="yh-label">Host</label>
            {hostLocked ? (
              <div className="yh-input flex min-h-[56px] w-full items-center justify-between gap-2 !cursor-default !bg-[#F9FAFB] !py-2 text-left">
                {selectedHost ? (
                  <HostRow user={selectedHost} selected />
                ) : (
                  <span className="text-[#9CA3AF]">Loading host…</span>
                )}
                <span className="shrink-0 rounded-md bg-[#F9F0F4] px-2 py-0.5 text-[11px] font-semibold text-[#A02448]">
                  You
                </span>
              </div>
            ) : (
              <>
                <button
                  type="button"
                  aria-haspopup="listbox"
                  aria-expanded={hostOpen}
                  onClick={() => setHostOpen((v) => !v)}
                  className={`yh-input flex min-h-[56px] w-full items-center justify-between gap-2 !py-2 text-left ${
                    hostOpen
                      ? "!border-[#841C39] shadow-[0_0_0_2px_rgba(132,28,57,0.12)]"
                      : ""
                  }`}
                >
                  {selectedHost ? (
                    <HostRow user={selectedHost} selected />
                  ) : (
                    <span className="text-[#9CA3AF]">Select host</span>
                  )}
                  <span className="shrink-0 text-[#6B7280]">
                    <ChevronIcon open={hostOpen} />
                  </span>
                </button>

                {hostOpen ? (
                  <div className="absolute left-0 right-0 z-[1400] mt-1.5 overflow-hidden rounded-xl border border-[#EEF0F5] bg-white shadow-[0_12px_32px_rgba(15,23,42,0.12)]">
                    <div className="border-b border-[#EEF0F5] p-2.5">
                      <div className="relative">
                        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]">
                          <SearchIcon />
                        </span>
                        <input
                          ref={hostSearchRef}
                          type="text"
                          value={hostQuery}
                          onChange={(e) => setHostQuery(e.target.value)}
                          placeholder="Search host..."
                          className="w-full rounded-lg border border-[#E5E7EB] bg-white py-2 pl-9 pr-3 text-sm text-[#111827] outline-none placeholder:text-[#9CA3AF] focus:border-[#841C39]"
                        />
                      </div>
                    </div>
                    <ul
                      role="listbox"
                      className="max-h-72 overflow-y-auto py-1 [scrollbar-width:thin]"
                    >
                      {filteredHosts.length === 0 ? (
                        <li className="px-3 py-4 text-center text-sm text-[#9CA3AF]">
                          No results found
                        </li>
                      ) : (
                        filteredHosts.map((user) => {
                          const isActive = String(user.id) === hostValue;
                          return (
                            <li key={user.id}>
                              <button
                                type="button"
                                role="option"
                                aria-selected={isActive}
                                className={`flex w-full px-3 py-2.5 text-left transition ${
                                  isActive
                                    ? "bg-[#F3F4F6]"
                                    : "bg-white hover:bg-[#F3F4F6]"
                                }`}
                                onClick={() => {
                                  props.setStateData({ host: user.id });
                                  setHostOpen(false);
                                }}
                              >
                                <HostRow user={user} selected={isActive} />
                              </button>
                            </li>
                          );
                        })
                      )}
                    </ul>
                  </div>
                ) : null}
              </>
            )}
          </div>

          {projects?.length ? (
            <div>
              <p className="mb-2 text-sm font-semibold text-[#111827]">
                Your Projects
              </p>
              <div className="flex flex-wrap gap-2">
                {projects.map((project: any, index: number) => (
                  <span
                    key={index}
                    className="inline-flex rounded-lg bg-[#F9F0F4] px-2.5 py-1 text-xs font-semibold text-[#A02448]"
                  >
                    {project.project_name}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <div>
            <p className="mb-2 text-sm font-semibold text-[#111827]">Calendar</p>
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#E5E7EB] bg-[#FAFAFA] p-4">
              <div className="flex min-w-0 items-center gap-3">
                <GoogleSvg />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#111827]">
                    Google Calendar
                  </p>
                  <p className="truncate text-xs text-[#6B7280]">
                    {props.state?.calender?.length
                      ? props.state.calender.join(", ")
                      : "Not connected"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={initiateGoogleAuth}
                className={`inline-flex h-9 shrink-0 items-center rounded-xl px-3 text-sm font-semibold transition ${
                  props.state?.calender?.length
                    ? "border border-[#E5E7EB] bg-white text-[#374151] hover:bg-[#F3F4F6]"
                    : "bg-[#A02448] text-white hover:bg-[#841C39]"
                }`}
              >
                {props.state?.calender?.length ? "Reconnect" : "Connect"}
              </button>
            </div>
            <p className="mt-2 text-xs text-[#9CA3AF]">
              We use your calendar to check availability and schedule meetings.
            </p>
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-[#EEF0F5] bg-white px-5 py-4 sm:px-6">
        <YhButton
          variant="secondary"
          fullWidth={false}
          onClick={goBack}
          className="!w-auto"
        >
          Go Back
        </YhButton>
        <YhButton
          variant="primary"
          fullWidth={false}
          onClick={submitForm}
          className="!w-auto"
        >
          {props.userId ? "Continue →" : "Save & Continue →"}
        </YhButton>
      </div>
    </div>
  );
};

export default BasicSetup;
